import { EVENT_TYPE_LABELS, RISK_LEVEL_LABELS } from './securityLabels';

interface Props {
  searchTerm: string;
  onSearchChange: (v: string) => void;
  activeType: string;
  onTypeChange: (v: string) => void;
  activeRisk: string;
  onRiskChange: (v: string) => void;
  activeCountry: string;
  onCountryChange: (v: string) => void;
  dateFrom: string;
  onDateFromChange: (v: string) => void;
  dateTo: string;
  onDateToChange: (v: string) => void;
  ipFilter: string;
  onIpFilterChange: (v: string) => void;
  typeCounts: Record<string, number>;
  riskCounts: Record<string, number>;
  countries: string[];
}

export default function SecurityFilters({
  searchTerm,
  onSearchChange,
  activeType,
  onTypeChange,
  activeRisk,
  onRiskChange,
  activeCountry,
  onCountryChange,
  dateFrom,
  onDateFromChange,
  dateTo,
  onDateToChange,
  ipFilter,
  onIpFilterChange,
  typeCounts,
  riskCounts,
  countries,
}: Props) {
  const typeOptions = [
    { value: 'all', label: 'Todos' },
    ...Object.entries(EVENT_TYPE_LABELS).map(([k, v]) => ({ value: k, label: v.label })),
  ];

  const riskOptions = [
    { value: 'all', label: 'Todos' },
    ...Object.entries(RISK_LEVEL_LABELS).map(([k, v]) => ({ value: k, label: v.label })),
  ];

  return (
    <div className="bg-background-50 border border-background-200 rounded-2xl p-4 space-y-4">
      {/* Search + IP */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="flex-1 relative">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-300 text-sm"></i>
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder="Buscar por usuario, radio, IP, país, detalles..."
            className="w-full pl-9 pr-3 py-2 text-sm rounded-xl bg-background-100 border border-background-200 text-foreground-800 placeholder:text-foreground-300 outline-none focus:border-foreground-300 transition-colors"
          />
        </div>
        <div className="relative sm:w-48">
          <i className="ri-global-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-300 text-sm"></i>
          <input
            type="text"
            value={ipFilter}
            onChange={(e) => onIpFilterChange(e.target.value)}
            placeholder="Filtrar por IP..."
            className="w-full pl-9 pr-3 py-2 text-sm rounded-xl bg-background-100 border border-background-200 text-foreground-800 placeholder:text-foreground-300 outline-none focus:border-foreground-300 transition-colors"
          />
        </div>
      </div>

      {/* Type chips */}
      <div>
        <p className="text-foreground-400 text-[10px] font-semibold uppercase tracking-wider mb-2">Tipo de evento</p>
        <div className="flex flex-wrap gap-1.5">
          {typeOptions.map((opt) => (
            <button
              key={opt.value}
              onClick={() => onTypeChange(opt.value)}
              className={`flex items-center gap-1 px-2.5 py-1 rounded-lg text-[11px] font-medium transition-all cursor-pointer whitespace-nowrap ${
                activeType === opt.value
                  ? 'bg-foreground-950 text-background-50'
                  : 'bg-background-100 text-foreground-500 hover:bg-background-200'
              }`}
            >
              {opt.label}
              {typeCounts[opt.value] !== undefined && (
                <span className={`text-[9px] ${activeType === opt.value ? 'text-background-50/70' : 'text-foreground-300'}`}>
                  {typeCounts[opt.value]}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Risk + Country + Date row */}
      <div className="flex flex-col sm:flex-row gap-3">
        {/* Risk chips */}
        <div className="flex-1">
          <p className="text-foreground-400 text-[10px] font-semibold uppercase tracking-wider mb-2">Nivel de riesgo</p>
          <div className="flex flex-wrap gap-1.5">
            {riskOptions.map((opt) => (
              <button
                key={opt.value}
                onClick={() => onRiskChange(opt.value)}
                className={`flex items-center gap-1 px-2.5 py-1 rounded-lg text-[11px] font-medium transition-all cursor-pointer whitespace-nowrap ${
                  activeRisk === opt.value
                    ? 'bg-foreground-950 text-background-50'
                    : 'bg-background-100 text-foreground-500 hover:bg-background-200'
                }`}
              >
                {opt.label}
                {riskCounts[opt.value] !== undefined && (
                  <span className={`text-[9px] ${activeRisk === opt.value ? 'text-background-50/70' : 'text-foreground-300'}`}>
                    {riskCounts[opt.value]}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Country */}
        <div className="sm:w-44">
          <p className="text-foreground-400 text-[10px] font-semibold uppercase tracking-wider mb-2">País</p>
          <select
            value={activeCountry}
            onChange={(e) => onCountryChange(e.target.value)}
            className="w-full px-3 py-2 text-sm rounded-xl bg-background-100 border border-background-200 text-foreground-800 outline-none focus:border-foreground-300 transition-colors cursor-pointer"
          >
            <option value="all">Todos los países</option>
            {countries.map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
        </div>

        {/* Date range */}
        <div className="flex items-end gap-2">
          <div>
            <p className="text-foreground-400 text-[10px] font-semibold uppercase tracking-wider mb-2">Desde</p>
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => onDateFromChange(e.target.value)}
              className="w-full px-3 py-2 text-sm rounded-xl bg-background-100 border border-background-200 text-foreground-800 outline-none focus:border-foreground-300 transition-colors"
            />
          </div>
          <div>
            <p className="text-foreground-400 text-[10px] font-semibold uppercase tracking-wider mb-2">Hasta</p>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => onDateToChange(e.target.value)}
              className="w-full px-3 py-2 text-sm rounded-xl bg-background-100 border border-background-200 text-foreground-800 outline-none focus:border-foreground-300 transition-colors"
            />
          </div>
        </div>
      </div>
    </div>
  );
}